import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ArrowRight, Home } from "lucide-react";
import { PageHero } from "@/components/Section";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { FoundationLink } from "@/components/FoundationLink";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(check, 1600);
      } catch (err) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(check, 1600);
      }
    };
    check();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page">
      <PageHero overline="Thank You" title="Payment" accent="Complete" subtitle="" image={IMAGES.trophies} />
      <section className="paper-noise bg-background">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center relative z-10">
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 size={44} className="mx-auto text-rust animate-spin" />
              <h2 className="mt-6 font-serif font-black text-3xl text-espresso">Confirming your payment…</h2>
              <p className="mt-3 text-muted-foreground">This only takes a moment. Please don't close this window.</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-paid">
              <CheckCircle2 size={52} className="mx-auto text-rust" />
              <h2 className="mt-6 font-serif font-black text-4xl text-espresso">You're All Set!</h2>
              <p className="mt-4 text-muted-foreground">
                Your payment was received and a confirmation email is on its way. Thank you for supporting the Wayne Walker Foundation and our first responders.
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Curious about the Foundation's year-round work?{" "}
                <FoundationLink testId="payment-foundation-site-link" className="text-rust font-medium hover:underline">Visit waynewalkerfoundation.org</FoundationLink>
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso flex items-center gap-2 hard-shadow-sm hover:-translate-y-0.5 transition-transform"><Home size={16} /> Back Home</Link>
                <Link to="/event" className="label-font text-sm border border-espresso px-8 py-4 text-espresso flex items-center gap-2 hover:bg-espresso/5">Event Details <ArrowRight size={16} /></Link>
              </div>
            </div>
          )}
          {state === "pending" && (
            <div data-testid="payment-pending">
              <Loader2 size={44} className="mx-auto text-rust" />
              <h2 className="mt-6 font-serif font-black text-3xl text-espresso">Almost There</h2>
              <p className="mt-3 text-muted-foreground">
                Your payment is processing. If you completed checkout, you'll receive a confirmation email shortly. Questions? Contact us and reference your email address.
              </p>
              <Link to="/" className="mt-8 inline-flex label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso items-center gap-2 hard-shadow-sm">Back Home</Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="payment-error">
              <XCircle size={48} className="mx-auto text-espresso/60" />
              <h2 className="mt-6 font-serif font-black text-3xl text-espresso">We Couldn't Confirm This</h2>
              <p className="mt-3 text-muted-foreground">We weren't able to verify a payment for this link. If you were charged, please reach out and we'll sort it out right away.</p>
              <Link to="/" className="mt-8 inline-flex label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso items-center gap-2 hard-shadow-sm">Back Home</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page">
      <PageHero overline="Checkout" title="Payment" accent="Canceled" subtitle="" image={IMAGES.golferSwing} />
      <section className="paper-noise bg-background">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center relative z-10">
          <XCircle size={48} className="mx-auto text-espresso/60" />
          <h2 className="mt-6 font-serif font-black text-4xl text-espresso">No Charge Was Made</h2>
          <p className="mt-4 text-muted-foreground">Your checkout was canceled and you have not been charged. Whenever you're ready, you can pick up right where you left off.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-espresso flex items-center gap-2 hard-shadow-sm hover:-translate-y-0.5 transition-transform">Register a Team <ArrowRight size={16} /></Link>
            <Link to="/donate" className="label-font text-sm border border-espresso px-8 py-4 text-espresso flex items-center gap-2 hover:bg-espresso/5">Make a Donation</Link>
            <Link to="/sponsor" className="label-font text-sm border border-espresso px-8 py-4 text-espresso flex items-center gap-2 hover:bg-espresso/5">Become a Sponsor</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
